import './upgrade_site_viewer_version_modal.less';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';

import map from 'lodash/map';
import isNil from 'lodash/isNil';

import ListBox from 'zoomforth_component_lib/listbox.jsx';
import PagesInUse from 'zoomforth_component_lib/pages_in_use.jsx';
import TruncatedText from 'zoomforth_component_lib/truncated_text.jsx';
import ThemeStyleSheetStatus from 'zoomforth_component_lib/theme_stylesheet_status.jsx';

import {
  LayerIcon,
  ZoomforthLogoIcon
} from 'zoomforth_component_lib/app_modern_icons.jsx';

import {
  ModernModalWithHeader,
  ModernModalSteppedActions
} from 'zoomforth_component_lib/modern_modal_with_header.jsx';

import MapStylePresetsDisplay
  from 'zoomforth_codesbane_editor/component_lib/map_style_presets_display_container.jsx';


const START_UPGRADE_VIEW = 'START_UPGRADE_VIEW';
const PUBLISH_SITE_VIEW = 'PUBLISH_SITE_VIEW';
const ALREADY_PUBLISHED_SITE_VIEW = 'ALREADY_PUBLISHED_SITE_VIEW';
const CHOOSE_THEME_VIEW = 'CHOOSE_THEME_VIEW';
const THEME_IS_COMPATIBLE_VIEW = 'THEME_IS_COMPATIBLE_VIEW';
const STYLE_PRESET_MAPPING_VIEW = 'STYLE_PRESET_MAPPING_VIEW';
const FINALIZE_UPGRADE_VIEW = 'FINALIZE_UPGRADE_VIEW';

class UpgradeSiteViewerVersionModal extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isLoadingPresetData: false,
      newThemeData: {},
      mappedStylePresets: {},
      isMappingStylePresetsRequired: false,
      currentView: START_UPGRADE_VIEW,
      selectedThemeUuid: (
        props.isCurrentThemeCompatibleForUpgrade ?
          props.currentThemeUuid
        :
          null
      )
    };

    autobind(this);
  }

  componentDidUpdate(prevProps) {
    // sometimes themes take longer to load so we don't know if the current
    // theme is ready for the upgrade yet
    if (
      prevProps.isCurrentThemeCompatibleForUpgrade === false &&
      this.props.isCurrentThemeCompatibleForUpgrade === true
    ) {
      this.setState({
        selectedThemeUuid: this.props.currentThemeUuid
      });
    }
  }

  handleCurrentViewChange(nextView) {
    this.setState({ currentView: nextView });
  }

  handleThemeSelect(selectedThemeUuid) {
    this.setState({
      selectedThemeUuid,
      // reset the preset metadata so we can determine it for the new theme
      newThemeData: null,
      isMappingStylePresetsRequired: false,
      isLoadingPresetData: true
    });

    this.props.onThemeSelect(selectedThemeUuid, {
      onSuccess: this.handleStylePresetMetaFetchSuccess
    });
  }

  handleStylePresetMetaFetchSuccess({ newThemeData, isMappingStylePresetsRequired }) {
    this.setState({
      newThemeData,
      isMappingStylePresetsRequired,
      isLoadingPresetData: false
    });
  }

  handleStylePresetMappingChange(mappedStylePresets) {
    this.setState({ mappedStylePresets });
  }

  handleUpgrade({ shouldMapPresets = true } = {}) {
    const { onUpgradeClick } = this.props;
    const { selectedThemeUuid, mappedStylePresets } = this.state;

    onUpgradeClick(
      selectedThemeUuid,
      shouldMapPresets ? mappedStylePresets : null
    );

    this.handleCurrentViewChange(FINALIZE_UPGRADE_VIEW);
  }

  render() {
    const {
      currentView,
      newThemeData,
      selectedThemeUuid,
      mappedStylePresets,
      isLoadingPresetData,
      isMappingStylePresetsRequired
    } = this.state;

    const {
      currentThemeEditorUrl,
      siteViewerVersionIntro,
      siteViewerVersionDescription,
      isLoadingThemes,
      isCurrentThemeCompatibleForUpgrade,
      isPublishAvailable,
      compatibleThemes,
      onPublishClick,
      onPreviewClick,
      onRequestClose,
      onDismissClick
    } = this.props;

    const totalSteps = isCurrentThemeCompatibleForUpgrade ? 2 : 3;

    return (
      <ModernModalWithHeader
        show={true}
        title="Upgrade your site"
        isExpanded={currentView === STYLE_PRESET_MAPPING_VIEW}
        className="site-viewer-version-modal"
        onRequestClose={onRequestClose}
      >
        {

          currentView === START_UPGRADE_VIEW ?

            <StartUpgradeView
              siteViewerVersionIntro={siteViewerVersionIntro}
              siteViewerVersionDescription={siteViewerVersionDescription}
              shouldShowStepIndicator={false}
              totalSteps={totalSteps}
              isRightBtnDisabled={isLoadingThemes}
              rightBtnDisabledReason={isLoadingThemes ? 'We are checking your themes, this may take a moment.' : null}
              onLeftBtnClick={onDismissClick}
              onRightBtnClick={()=> {
                this.handleCurrentViewChange(
                  isPublishAvailable ?
                    PUBLISH_SITE_VIEW
                  :
                    ALREADY_PUBLISHED_SITE_VIEW
                );
              }}
            />

          : currentView === PUBLISH_SITE_VIEW ?

            <PublishSiteView
              totalSteps={totalSteps}
              onLeftBtnClick={()=> {
                this.handleCurrentViewChange(
                  isCurrentThemeCompatibleForUpgrade ?
                    THEME_IS_COMPATIBLE_VIEW
                  :
                    CHOOSE_THEME_VIEW
                );
              }}
              onRightBtnClick={()=> {
                onPublishClick();

                this.handleCurrentViewChange(
                  isCurrentThemeCompatibleForUpgrade ?
                    THEME_IS_COMPATIBLE_VIEW
                  :
                    CHOOSE_THEME_VIEW
                );
              }}
            />

          : currentView === ALREADY_PUBLISHED_SITE_VIEW ?

            <AlreadyPublishedSiteView
              totalSteps={totalSteps}
              onLeftBtnClick={onRequestClose}
              onRightBtnClick={()=> {
                this.handleCurrentViewChange(
                  isCurrentThemeCompatibleForUpgrade ?
                    THEME_IS_COMPATIBLE_VIEW
                  :
                    CHOOSE_THEME_VIEW
                );
              }}
            />

          : currentView === CHOOSE_THEME_VIEW ?

            <ChooseThemeView
              currentThemeEditorUrl={currentThemeEditorUrl}
              selectedThemeUuid={selectedThemeUuid}
              compatibleThemes={compatibleThemes}
              totalSteps={totalSteps}
              isLeftBtnDisabled={isNil(selectedThemeUuid)}
              isRightBtnDisabled={isNil(selectedThemeUuid) || isNil(newThemeData)}
              leftBtnDisabledReason={isNil(selectedThemeUuid) ? 'You must select a theme to preview it' : null}
              rightBtnDisabledReason={
                isNil(selectedThemeUuid) ?
                  'You must select a theme to continue'
                : isLoadingPresetData ?
                  'We are looking up information about this theme'
                :
                  null
              }
              onLeftBtnClick={()=> {
                onPreviewClick(selectedThemeUuid);
              }}
              onRightBtnClick={()=> {
                if (isMappingStylePresetsRequired) {
                  this.handleCurrentViewChange(STYLE_PRESET_MAPPING_VIEW);
                } else {
                  this.handleCurrentViewChange(FINALIZE_UPGRADE_VIEW);
                }
              }}
              onThemeSelect={this.handleThemeSelect}
            />

          : currentView === THEME_IS_COMPATIBLE_VIEW ?

            <ThemeIsCompatibleView
              totalSteps={totalSteps}
              onLeftBtnClick={onRequestClose}
              onRightBtnClick={this.handleUpgrade}
            />

          : currentView === STYLE_PRESET_MAPPING_VIEW ?

            <StylePresetMappingView
              mappedStylePresets={mappedStylePresets}
              newThemeData={newThemeData}
              totalSteps={totalSteps}
              onStylePresetMappingChange={this.handleStylePresetMappingChange}
              onLeftBtnClick={()=> {
                this.handleUpgrade({ shouldMapPresets: false });
              }}
              onRightBtnClick={this.handleUpgrade}
            />

          : currentView === FINALIZE_UPGRADE_VIEW ?

            <FinalizeUpgradeView
              totalSteps={totalSteps}
              isLeftBtnDisabled={isNil(selectedThemeUuid)}
              leftBtnDisabledReason={isNil(selectedThemeUuid) ? 'You must select a theme to preview it' : null}
              onLeftBtnClick={()=> {
                onPreviewClick(selectedThemeUuid);
              }}
              onRightBtnClick={onRequestClose}
            />

          :

            null // in practice this never happens

        }
      </ModernModalWithHeader>
    );
  }

}

const ModalButtons = ({
  shouldShowStepIndicator,
  currentStepNum,
  totalSteps,
  leftBtnContent,
  rightBtnContent,
  leftBtnDisabledReason,
  rightBtnDisabledReason,
  isLeftBtnDisabled,
  isRightBtnDisabled,
  onLeftBtnClick,
  onRightBtnClick
})=> (
  <ModernModalSteppedActions
    className="upgrade-stylesheet-actions"
    {...{
      shouldShowStepIndicator,
      currentStepNum,
      totalSteps,
      leftBtnContent,
      rightBtnContent,
      leftBtnDisabledReason,
      rightBtnDisabledReason,
      isLeftBtnDisabled,
      isRightBtnDisabled,
      onLeftBtnClick,
      onRightBtnClick
    }}
  />
);

const ThemeOption = ({ theme })=> {
  return (
    <div className="theme-option">
      <div className="theme-preview-thumbnail">
        {
          theme.screenshotUrl ?
            <img src={theme.screenshotUrl} />
          :
            <ZoomforthLogoIcon />
        }
      </div>

      <div className="theme-metadata">
        <div className="theme-title">
          <TruncatedText
            tooltipPlacement="topLeft"
            maxWidth="85%"
            isTooltipEnabled={false}
          >
            { theme.name || 'Untitled Theme' }
          </TruncatedText>
        </div>

        <div className="theme-description">
          <TruncatedText
            tooltipPlacement="topLeft"
            maxWidth="100%"
            isTooltipEnabled={false}
          >
            { theme.description || 'No description' }
          </TruncatedText>
        </div>

        <div className="theme-status">
          <ThemeStyleSheetStatus theme={theme} />

          <span className="is-compatible-status">
            <LayerIcon /> Compatible
          </span>
        </div>
      </div>

      <div className="theme-pages-in-use">
        <PagesInUse
          label={`${theme.pagesUsingTheme} Site${theme.pagesUsingTheme === 1 ? '' : 's'}`}
        />
      </div>
    </div>
  );
};

const StartUpgradeView = ({
  siteViewerVersionIntro,
  siteViewerVersionDescription,
  ...btnProps
})=> (
  <div className="site-viewer-version-start-upgrade-view">
    <p>{ siteViewerVersionIntro }</p>

    <p>{ siteViewerVersionDescription }</p>

    <ModalButtons
      currentStepNum={1}
      leftBtnContent="Maybe later"
      rightBtnContent="Start upgrade"
      {...btnProps}
    />
  </div>
);

const PublishSiteView = ({ ...btnProps })=> (
  <div className="site-viewer-version-publish-site-view">
    <h2>Step 1: Publish your site</h2>

    <p>
      If you have made changes to your site since publishing, you may wish to
      publish your site in order to save your work before you make this
      upgrade.
    </p>

    <p>
      The upgrade itself will not cause your site changes to be published.
    </p>

    <ModalButtons
      currentStepNum={1}
      leftBtnContent="Skip"
      rightBtnContent="Publish"
      {...btnProps}
    />
  </div>
);

const AlreadyPublishedSiteView = ({ ...btnProps })=> (
  <div className="site-viewer-version-publish-site-view">
    <h2>Step 1: Publish your site</h2>

    <p>
      Your site has no unpublished changes. The upgrade itself will add
      unpublished changes to this site.
    </p>

    <ModalButtons
      currentStepNum={1}
      leftBtnContent="Cancel"
      rightBtnContent="Next"
      {...btnProps}
    />
  </div>
);

const ThemeEditorConditionalLink = ({ currentThemeEditorUrl })=> (
  !isNil(currentThemeEditorUrl) ?
    <a href={currentThemeEditorUrl}>theme editor</a>
  :
    'theme editor'
);

const ChooseThemeView = ({
  currentThemeEditorUrl,
  selectedThemeUuid,
  compatibleThemes,
  onThemeSelect,
  ...btnProps
})=> (
  <div className="site-viewer-version-choose-theme-view">
    <h2>Step 2: Choose a compatible theme</h2>

    <p>
      This upgrade requires you to use an upgraded theme stylesheet. You may
      select a new theme below or upgrade this theme in the <ThemeEditorConditionalLink currentThemeEditorUrl={currentThemeEditorUrl} />.
      After selecting the theme you will be able to preview the site before
      completing the upgrade.
    </p>

    <ListBox
      label="Select a compatible theme"
      labelId="select-theme-for-upgrade-id"
      placeholder='Select a theme'
      className="select-theme-for-upgrade"
      selectedValue={selectedThemeUuid}
      options={map(compatibleThemes, (theme)=> {
        return { 
          value: theme.uuid,
          label: theme.name,
          content: <ThemeOption theme={theme} />
        };
      })}
      onChange={(themeName)=> {
        onThemeSelect(themeName);
      }}
    />

    <ModalButtons
      currentStepNum={2}
      leftBtnContent="Preview"
      rightBtnContent="Next"
      {...btnProps}
    />
  </div>
);

const ThemeIsCompatibleView = ({ ...btnProps })=> (
  <div className="site-viewer-version-theme-is-compatible-view">
    <h2>Step 2: Upgrade your site now</h2>

    <p>
      We have checked the theme linked to this site and it is compatible with
      this upgrade process.
    </p>

    <ModalButtons
      currentStepNum={2}
      leftBtnContent="Cancel"
      rightBtnContent="Upgrade now"
      {...btnProps}
    />
  </div>
);

const StylePresetMappingView = ({ newThemeData, onStylePresetMappingChange, ...btnProps })=> (
  <div className="site-viewer-version-style-preset-mapping-view">
    <h2>Step 3: Apply theme</h2>

    <p>
      Drag to reorder style presets so old presets are appropriately linked to
      new presets.
    </p>

    <MapStylePresetsDisplay
      newThemeData={newThemeData}
      onStylePresetMappingChange={onStylePresetMappingChange}
    />

    <ModalButtons
      currentStepNum={3}
      leftBtnContent="Apply without mapping"
      rightBtnContent="Apply with mapping"
      {...btnProps}
    />
  </div>
);

const FinalizeUpgradeView = ({ ...btnProps })=> (
  <div className="site-viewer-version-finalize-upgrade-view">
    <h2>Your site is now upgraded</h2>

    <p>
      Your upgrade is complete and your site is now using the most recent
      version of our software.
    </p>

    <p>
      Please check your site to ensure it looks right.
    </p>

    <p>
      If you are not happy with the changes, you can revert your site to the
      last published version by clicking on the revert button at the top of the
      editor.
    </p>

    <ModalButtons
      currentStepNum={btnProps.totalSteps}
      leftBtnContent="Preview"
      rightBtnContent="Done"
      {...btnProps}
    />
  </div>
);

UpgradeSiteViewerVersionModal.propTypes = {
  siteViewerVersionIntro: PropTypes.string.isRequired,
  siteViewerVersionDescription: PropTypes.string.isRequired,
  isLoadingThemes: PropTypes.bool.isRequired,
  isPublishAvailable: PropTypes.bool.isRequired,
  isCurrentThemeCompatibleForUpgrade: PropTypes.bool.isRequired,
  compatibleThemes: PropTypes.array.isRequired,
  onPublishClick: PropTypes.func.isRequired,
  onPreviewClick: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onDismissClick: PropTypes.func.isRequired,
  onThemeSelect: PropTypes.func.isRequired
};

export default UpgradeSiteViewerVersionModal;
